import type { ChangeEvent, ComponentType, ReactNode } from 'react';
import { StarIcon } from '../../assets/icons/StarIcon';
import { PlusIcon } from '../../assets/icons/PlusIcon';
import CouponImage from '../../assets/images/coupon.png';
import Select from './Select';
import {
  Image,
  Text,
  Textarea,
  Input,
  RadioGroup,
  LucideIcon,
  HStack,
  VStack,
  useBreakpointValue,
} from '@skin-seoul/design-system';
import {
  Section,
  Star,
  FileInput,
  Row,
  SubmitButton,
  ImageUploadBox,
  Thumbnail,
  ThumbnailImage,
  RemoveButton,
  UploadArea,
  SmallUploadArea,
} from './SharedStyles';

interface SectionProps {
  children: ReactNode;
  style?: React.CSSProperties;
}

interface RenderProductIntroProps {
  productInfo: {
    name: string;
    brand: string;
    image: string;
    category: string;
  };
}

interface RenderRatingSectionProps {
  rating: number;
  setRating: (rating: number) => void;
  SectionComponent?: ComponentType<SectionProps>;
}

interface RenderSkinInfoSectionProps {
  skinType: string;
  setSkinType: (value: string) => void;
  skinConcern: string;
  setSkinConcern: (value: string) => void;
  skinTypeOptions: Array<{ value: string; label: string }>;
  skinConcernOptions: Array<{ value: string; label: string }>;
}

interface RenderReviewContentSectionProps {
  content: string;
  setContent: (content: string) => void;
}

interface RenderTitleSectionProps {
  title: string;
  setTitle: (title: string) => void;
}

interface RenderImageUploadSectionProps {
  images: File[];
  imagePreviews: string[];
  handleImageChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleRemoveImage: (idx: number) => void;
}

interface RenderRecommendationSectionProps {
  recommend: string;
  setRecommend: (value: string) => void;
}

interface RenderNicknameSectionProps {
  nickname: string;
  setNickname: (nickname: string) => void;
}

interface RenderEmailSectionProps {
  email: string;
  setEmail: (email: string) => void;
}

interface RenderSubmitSectionProps {
  isSubmitDisabled: boolean;
  onSubmit: () => void;
}

export const renderProductIntro = ({ productInfo }: RenderProductIntroProps) => (
  <HStack gap={6} align="center" w="full">
    <Image
      src={productInfo.image}
      w={{
        base: '88px',
        tablet: '136px',
      }}
      h={{
        base: '88px',
        tablet: '136px',
      }}
      alt={productInfo.name}
      flexShrink={0}
      borderRadius="8px"
      overflow="hidden"
    />
    <VStack align="start" gap={2}>
      <HStack gap={2} align="center">
        <Text textStyle="label.medium" fontWeight="semibold" color="neutral.700">
          {productInfo.brand}
        </Text>
        <Text textStyle="label.small" fontWeight="medium" color="primary.400">
          {productInfo.category}
        </Text>
      </HStack>
      <Text textStyle="paragraph.small" fontWeight="normal" color="neutral.800">
        {productInfo.name}
      </Text>
    </VStack>
  </HStack>
);

export const renderRatingSection = ({
  rating,
  setRating,
  SectionComponent: _SectionComponent = Section,
}: RenderRatingSectionProps) => (
  <VStack
    gap={{
      base: 3,
      tablet: 3,
    }}
    align="start"
    w="full"
  >
    <Text textStyle="label.medium" fontWeight="medium">
      Overall rating*
    </Text>
    <HStack w="full" align="center" justify="center">
      <HStack align="center" gap={1} justify="center">
        {[1, 2, 3, 4, 5].map((num) => (
          <Star
            key={num}
            type="button"
            selected={rating >= num}
            onClick={() => setRating(num)}
            aria-label={`Rating ${num} stars`}
          >
            <StarIcon fillColor="#6F3FD7" isFill={rating >= num} />
          </Star>
        ))}
      </HStack>
    </HStack>
  </VStack>
);

export const renderSkinInfoSection = ({
  skinType,
  setSkinType,
  skinConcern,
  setSkinConcern,
  skinTypeOptions,
  skinConcernOptions,
}: RenderSkinInfoSectionProps) => (
  <VStack gap={{ base: 3, tablet: 3 }} align="start" w="full">
    <Text textStyle="label.medium" fontWeight="medium">
      More about you*
    </Text>
    <Row>
      <Section style={{ flex: 1, marginBottom: 0 }}>
        <Select options={skinTypeOptions} value={skinType} onChange={setSkinType} title="Skin type" showSelectedValue />
      </Section>
      <Section style={{ flex: 1, marginBottom: 0 }}>
        <Select
          options={skinConcernOptions}
          value={skinConcern}
          onChange={setSkinConcern}
          title="Skin concern"
          showSelectedValue
        />
      </Section>
    </Row>
  </VStack>
);

export const renderReviewContentSection = ({ content, setContent }: RenderReviewContentSectionProps) => (
  <VStack
    gap={{
      base: 3,
      tablet: 3,
    }}
    align="start"
    w="full"
  >
    <Text textStyle="label.medium" fontWeight="medium">
      Your review*
    </Text>
    <Textarea
      id="review-content"
      value={content}
      onChange={(e) => setContent(e.target.value)}
      w="full"
      height={16}
      borderColor="neutral.100"
    />
  </VStack>
);

export const renderTitleSection = ({ title, setTitle }: RenderTitleSectionProps) => (
  <VStack
    gap={{
      base: 3,
      tablet: 3,
    }}
    align="start"
    w="full"
  >
    <Text textStyle="label.medium" fontWeight="medium">
      Add a headline*
    </Text>
    <Input
      id="review-title"
      maxLength={200}
      value={title}
      onChange={(e) => setTitle(e.target.value)}
      w="full"
      placeholder="e.g. Super moisturizing, exactly what I needed"
      _placeholder={{
        color: 'neutral.500',
        textStyle: 'paragraph.small',
      }}
      borderColor="neutral.100"
    />
  </VStack>
);

export const renderImageUploadSection = ({
  images,
  imagePreviews,
  handleImageChange,
  handleRemoveImage,
}: RenderImageUploadSectionProps) => (
  <VStack
    gap={{
      base: 3,
      tablet: 3,
    }}
    align="start"
    w="full"
  >
    <Text textStyle="label.medium" fontWeight="medium">
      Add a Photo or Video
    </Text>
    <ImageUploadBox>
      {images.map((file, idx) => (
        <Thumbnail key={`${file.name}-${file.size}-${file.lastModified}`}>
          <ThumbnailImage src={imagePreviews[idx]} alt={`Review photo ${idx + 1}`} />
          <RemoveButton type="button" onClick={() => handleRemoveImage(idx)} aria-label="Remove photo">
            <LucideIcon name="X" w="14px" h="14px" color="generic.100" />
          </RemoveButton>
        </Thumbnail>
      ))}
      {images.length < 5 &&
        (images.length === 0 ? (
          <UploadArea htmlFor="review-image">
            <PlusIcon />
            <FileInput id="review-image" type="file" accept="image/*" multiple onChange={handleImageChange} />
          </UploadArea>
        ) : (
          <SmallUploadArea htmlFor="review-image">
            <PlusIcon />
            <FileInput id="review-image" type="file" accept="image/*" multiple onChange={handleImageChange} />
          </SmallUploadArea>
        ))}
    </ImageUploadBox>
  </VStack>
);

export const renderRecommendationSection = ({ recommend, setRecommend }: RenderRecommendationSectionProps) => (
  <VStack
    gap={{
      base: 3,
      tablet: 3,
    }}
    align="start"
    w="full"
  >
    <Text textStyle="label.medium" fontWeight="medium">
      Repurchase Intention*
    </Text>

    <RadioGroup.Root name="recommend" value={recommend}>
      <HStack gap={4}>
        <RadioGroup.Item
          value="yes"
          onClick={() => {
            setRecommend('yes');
          }}
        >
          <HStack gap={2}>
            <RadioGroup.ItemIndicator
              w="16px"
              h="16px"
              borderColor="primary.400"
              borderWidth="1px"
              borderRadius="50%"
              bg="transparent"
              css={{
                '& > .dot': {
                  background: 'primary.400',
                  scale: '0.6',
                  borderRadius: '10px',
                },
              }}
            />

            <RadioGroup.ItemText cursor="pointer" textStyle="paragraph.small">
              Yes
            </RadioGroup.ItemText>
          </HStack>
        </RadioGroup.Item>

        <RadioGroup.Item
          value="no"
          onClick={() => {
            setRecommend('no');
          }}
        >
          <HStack gap={2}>
            <RadioGroup.ItemIndicator
              w="16px"
              h="16px"
              borderColor="primary.400"
              borderWidth="1px"
              borderRadius="50%"
              bg="transparent"
              css={{
                '& > .dot': {
                  background: 'primary.400',
                  scale: '0.6',
                  borderRadius: '10px',
                },
              }}
            />

            <RadioGroup.ItemText cursor="pointer" textStyle="paragraph.small">
              No
            </RadioGroup.ItemText>
          </HStack>
        </RadioGroup.Item>
      </HStack>
    </RadioGroup.Root>
  </VStack>
);

export const renderNicknameSection = ({ nickname, setNickname }: RenderNicknameSectionProps) => (
  <VStack
    gap={{
      base: 3,
      tablet: 3,
    }}
    align="start"
    w="full"
  >
    <Text textStyle="label.medium" fontWeight="medium">
      Create a display name*
    </Text>
    <Input
      id="review-nickname"
      maxLength={25}
      value={nickname}
      onChange={(e) => setNickname(e.target.value)}
      w="full"
      placeholder="Enter your display name"
      _placeholder={{
        color: 'neutral.400',
      }}
      borderColor="neutral.100"
    />
  </VStack>
);

export const renderEmailSection = ({ email, setEmail }: RenderEmailSectionProps) => (
  <VStack
    gap={{
      base: 3,
      tablet: 3,
    }}
    align="start"
    w="full"
  >
    <Text textStyle="label.medium" fontWeight="medium">
      Email address*
    </Text>
    <Input
      id="review-email"
      type="email"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      w="full"
      placeholder="Enter your email address"
      _placeholder={{
        color: 'neutral.400',
      }}
      borderColor="neutral.100"
    />
  </VStack>
);

export const renderTermsSection = () => (
  <VStack
    gap={{
      base: 3,
      tablet: 3,
    }}
    align="start"
    w="full"
  >
    <HStack align="flex-start" gap={2}>
      <LucideIcon name="Info" w="16px" h="16px" color="neutral.500" />
      <Text textStyle="label.small" fontWeight="normal" color="neutral.500">
        We will email you to notify you that your review had been posted or if someone replies to your review.
      </Text>
    </HStack>
  </VStack>
);

export const renderSubmitSection = ({ isSubmitDisabled, onSubmit }: RenderSubmitSectionProps) => (
  <SubmitButton type="button" disabled={isSubmitDisabled} onClick={onSubmit}>
    Submit review
  </SubmitButton>
);

export const ReviewRewardBanner = () => {
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  return (
    <HStack
      padding={{
        base: '12px',
        tablet: '4px 4px 4px 12px',
      }}
      w="full"
      borderRadius="12px"
      gap={{
        base: 3,
        tablet: 6,
      }}
      align="center"
      justify="space-between"
      style={{
        background: 'linear-gradient(107deg,rgba(18, 0, 58, 1) 0%, rgba(92, 34, 219, 1) 133.32%)',
      }}
    >
      <VStack align="start">
        <VStack align="start" gap={0}>
          {isMobile ? (
            <>
              <Text textStyle="paragraph.overline.large" fontWeight="medium" whiteSpace="pre-wrap" color="generic.0">
                Review your purchase
              </Text>
              <Text textStyle="paragraph.overline.large" fontWeight="bold" whiteSpace="pre-wrap" color="generic.0">
                Get a coupon
              </Text>
              <Text textStyle="paragraph.overline.large" fontWeight="bold" whiteSpace="pre-wrap" color="generic.0">
                for your next order !
              </Text>
            </>
          ) : (
            <>
              <Text textStyle="paragraph.overline.large" fontWeight="medium" whiteSpace="pre-wrap" color="generic.0">
                Review your purchase
              </Text>
              <Text textStyle="paragraph.overline.large" fontWeight="bold" whiteSpace="pre-wrap" color="generic.0">
                Get a coupon for your next order !
              </Text>
            </>
          )}
        </VStack>
      </VStack>
      <Image src={CouponImage.src} alt="coin" w="130px" h="59px" flexShrink={0} />
    </HStack>
  );
};
